import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Switch,
  Alert,
  Snackbar,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

/**
 * CategoryManagerModal
 *
 * Props:
 * @param {boolean} open - 모달 열림 여부 [Required]
 * @param {function} onClose - 닫기 [Required]
 * @param {Array<{key:string, labels:{en:string, ko:string}, isActive:boolean, order?:number}>} categories - 현재 카테고리 목록 [Required]
 * @param {function} onCreate - (key: string, labels: {en, ko}) => Promise<void> [Required]
 * @param {function} onUpdate - (key: string, updates: Partial<{labels,isActive,order}>) => Promise<void> [Required]
 * @param {function} onDelete - (key: string) => Promise<void> [Required]
 */
function CategoryManagerModal({ open, onClose, categories, onCreate, onUpdate, onDelete }) {
  const [newKey, setNewKey] = useState('');
  const [newEn, setNewEn] = useState('');
  const [newKo, setNewKo] = useState('');
  // extra 제거
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (open) {
      setRows((categories || []).map((c) => ({
        key: c.key,
        en: c.labels?.en || '',
        ko: c.labels?.ko || '',
        isActive: c.isActive !== false,
        order: typeof c.order === 'number' ? c.order : '',
        _dirty: false,
      })));
      setNewKey('');
      setNewEn('');
      setNewKo('');
      
      setError('');
    }
  }, [open, categories]);

  const handleRowChange = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value, _dirty: true } : r));
  };

  // extra 제거로 관련 로직 삭제

  const handleCreate = async () => {
    try {
      setError('');
      const keyRegex = /^[A-Za-z0-9_-]{2,32}$/;
      if (!keyRegex.test(newKey)) {
        setError('키는 2~32자의 영문 대/소문자, 숫자, _ 또는 - 만 가능합니다.');
        return;
      }
      if (!newEn || !newKo) {
        setError('영문/한글 라벨은 필수입니다.');
        return;
      }
      const labels = { en: newEn.trim(), ko: newKo.trim() };
      await onCreate(newKey.trim(), labels);
      setNewKey(''); setNewEn(''); setNewKo('');
      setSnackbar({ open: true, message: '카테고리가 추가되었습니다.', severity: 'success' });
    } catch (e) {
      setError(e.message || '카테고리 생성 실패');
      setSnackbar({ open: true, message: e.message || '카테고리 생성 실패', severity: 'error' });
    }
  };

  const saveAll = async () => {
    try {
      setSaving(true);
      setError('');
      for (const r of rows) {
        if (!r._dirty) continue;
        if (!r.en || !r.ko) {
          throw new Error(`'${r.key}' 라벨(EN/KR)은 필수입니다.`);
        }
        const updates = {
          labels: { en: r.en, ko: r.ko },
          isActive: !!r.isActive,
        };
        if (r.order !== '' && !Number.isNaN(Number(r.order))) updates.order = Number(r.order);
        await onUpdate(r.key, updates);
      }
      setSnackbar({ open: true, message: '변경사항이 저장되었습니다.', severity: 'success' });
    } catch (e) {
      setError(e.message || '저장 실패');
      setSnackbar({ open: true, message: e.message || '저장 실패', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (key) => {
    if (!window.confirm('이 카테고리를 삭제하시겠습니까? 사용 중이면 삭제가 차단됩니다.')) return;
    try {
      await onDelete(key);
    } catch (e) {
      setError(e.message || '삭제 실패');
    }
  };

  const totalCount = useMemo(() => rows.length, [rows]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>카테고리 관리</DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>새 카테고리 추가</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Key" placeholder="Marketing" value={newKey} onChange={(e) => setNewKey(e.target.value)} fullWidth />
              <TextField label="Label (EN)" placeholder="Marketing" value={newEn} onChange={(e) => setNewEn(e.target.value)} fullWidth />
              <TextField label="라벨 (KO)" placeholder="마케팅" value={newKo} onChange={(e) => setNewKo(e.target.value)} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
              <Box sx={{ flex: 1 }} />
              <Button variant="contained" onClick={handleCreate}>추가</Button>
            </Stack>
          </Box>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Key</TableCell>
                  <TableCell>Label (EN)</TableCell>
                  <TableCell>라벨 (KO)</TableCell>
                  
                  <TableCell>Active</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, idx) => (
                  <TableRow key={r.key} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{r.key}</TableCell>
                    <TableCell>
                      <TextField value={r.en} onChange={(e) => handleRowChange(idx, 'en', e.target.value)} size="small" fullWidth />
                    </TableCell>
                    <TableCell>
                      <TextField value={r.ko} onChange={(e) => handleRowChange(idx, 'ko', e.target.value)} size="small" fullWidth />
                    </TableCell>
                    
                    <TableCell>
                      <Switch checked={!!r.isActive} onChange={(e) => handleRowChange(idx, 'isActive', e.target.checked)} />
                    </TableCell>
                    <TableCell>
                      <TextField value={r.order} onChange={(e) => handleRowChange(idx, 'order', e.target.value)} size="small" sx={{ width: 80 }} />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton color="error" onClick={() => handleDelete(r.key)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {totalCount === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary">카테고리가 없습니다.</Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
        <Button variant="contained" onClick={saveAll} disabled={saving}>{saving ? '저장 중...' : '변경사항 저장'}</Button>
      </DialogActions>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

export default CategoryManagerModal;


